import { Link } from "react-router-dom";
import Logo from "@/components/Logo";

const LegalShell = ({ title, kicker, children, testId }) => (
  <div className="min-h-screen bg-white" data-testid={testId}>
    <header className="border-b border-[var(--jc-border)]">
      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
        <Logo variant="full" theme="light" size="md" />
        <Link to="/" className="text-sm font-semibold text-[var(--jc-text-soft)] hover:text-[var(--jc-text)]">← Back to site</Link>
      </div>
    </header>
    <main className="max-w-3xl mx-auto px-6 lg:px-10 py-16">
      <div className="overline mb-3">{kicker}</div>
      <h1 className="font-display text-4xl lg:text-5xl tracking-tight">{title}</h1>
      <div className="text-sm text-[var(--jc-text-soft)] mt-2">Last updated: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</div>
      <div className="prose prose-slate max-w-none mt-10 text-[15px] leading-relaxed space-y-6">
        {children}
      </div>
    </main>
    <footer className="border-t border-[var(--jc-border)] py-10">
      <div className="max-w-4xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-[var(--jc-text-soft)]">
        <div>© {new Date().getFullYear()} JobClose AI · Built by a contractor, for contractors.</div>
        <div className="flex items-center gap-5">
          <Link to="/privacy" className="hover:text-[var(--jc-text)]">Privacy</Link>
          <Link to="/terms" className="hover:text-[var(--jc-text)]">Terms</Link>
          <Link to="/data-security" className="hover:text-[var(--jc-text)]">Data Security</Link>
        </div>
      </div>
    </footer>
  </div>
);

const H = ({ children }) => <h2 className="font-display text-xl mt-8 mb-2 text-[var(--jc-text)]">{children}</h2>;

export const PrivacyPolicy = () => (
  <LegalShell kicker="Legal" title="Privacy Policy" testId="privacy-page">
    <p>This Privacy Policy describes how JobClose AI ("we", "us", "our") collects, uses, and protects information when you use the JobClose AI platform.</p>
    <H>Information we collect</H>
    <p>We collect information you provide directly: account details (name, email, company name), pricing profile data, project and customer information you enter, voice transcripts and notes captured for estimates, and any uploads. We also collect basic usage data (logs, device, browser) for security and product improvement.</p>
    <H>How we use information</H>
    <ul className="list-disc pl-6 space-y-1">
      <li>To operate the JobClose AI platform and deliver requested features (estimates, proposals, contracts, invoices, customer updates).</li>
      <li>To run AI scope-organization and estimate-check features on the inputs you submit.</li>
      <li>To authenticate users and maintain account security.</li>
      <li>To improve and troubleshoot the product.</li>
    </ul>
    <H>AI processing</H>
    <p>When you click an AI feature (Organize Scope with AI, Run AI Estimate Check, or generate a customer update), the relevant inputs are sent to a third-party AI provider used by JobClose AI to produce a result. We do not use your business data to train third-party models, and we do not sell or share your data with other JobClose AI customers.</p>
    <H>Data sharing</H>
    <p>We do not sell your data. We share data only with vetted service providers necessary to run the platform (hosting, database, AI provider, payment processor) under contractual data-protection terms, or when required by law.</p>
    <H>Data retention</H>
    <p>We retain your account, project, and customer data for as long as your account is active. You can request export or deletion at any time by emailing support.</p>
    <H>Security</H>
    <p>We use industry-standard safeguards, including encrypted transport (HTTPS), password hashing (bcrypt), and access controls. No internet system is 100% secure; please use a strong, unique password.</p>
    <H>Your rights</H>
    <p>Depending on your jurisdiction, you may have the right to access, correct, export, or delete your personal data. Submit requests through the contact channel below.</p>
    <H>Contact</H>
    <p>Questions about this Privacy Policy: <span className="font-mono-jc">privacy@jobclose.ai</span></p>
  </LegalShell>
);

export const TermsOfUse = () => (
  <LegalShell kicker="Legal" title="Terms of Use" testId="terms-page">
    <p>These Terms of Use ("Terms") govern your access to and use of the JobClose AI platform. By creating an account or using the platform, you agree to these Terms.</p>
    <H>Eligibility</H>
    <p>You must be a contractor, business owner, or authorized team member age 18 or older to create an account.</p>
    <H>Your account</H>
    <p>You are responsible for the activity that happens under your account and for keeping your password confidential. Notify us immediately of any unauthorized use.</p>
    <H>Acceptable use</H>
    <ul className="list-disc pl-6 space-y-1">
      <li>Do not submit content that is illegal, infringing, defamatory, or violates someone's privacy.</li>
      <li>Do not attempt to reverse-engineer, attack, or disrupt the platform.</li>
      <li>Use AI features in good faith — final decisions on pricing, scope, and contract terms remain yours.</li>
    </ul>
    <H>AI outputs are not legal or professional advice</H>
    <p>Proposals, contracts, and estimate-check results produced by JobClose AI are drafts intended to support your judgment. They are not legal, accounting, or engineering advice. You should have qualified counsel and professionals review any contract or pricing before relying on it.</p>
    <H>Subscriptions and billing</H>
    <p>Paid subscriptions start at $129/month, which includes 2 full users, the complete JobClose AI product (no artificial feature-tier gating), and standard onboarding at no additional charge. Additional internal users are $20/month each. New accounts start with a 7-day free trial. Subscriptions renew monthly, and you may cancel anytime through your account; cancellation takes effect at the end of the current billing period. Optional paid migration or setup services may be offered separately. Payments are processed by Stripe; we do not store full payment-card data.</p>
    <H>Ownership</H>
    <p>You own the content you submit (project info, estimates, proposals, customer data). We own the JobClose AI platform, branding, and software. We grant you a non-exclusive license to use the platform during your subscription.</p>
    <H>Termination</H>
    <p>We may suspend or terminate accounts that violate these Terms. You may close your account at any time.</p>
    <H>Limitation of liability</H>
    <p>To the maximum extent permitted by law, JobClose AI is provided "as is" without warranties of any kind. Our aggregate liability is limited to the amount you paid us in the 12 months preceding the claim.</p>
    <H>Changes</H>
    <p>We may update these Terms from time to time. Material changes will be communicated through the platform or by email.</p>
    <H>Contact</H>
    <p>Questions about these Terms: <span className="font-mono-jc">legal@jobclose.ai</span></p>
  </LegalShell>
);

export const DataSecurity = () => (
  <LegalShell kicker="Trust" title="Data Security & Customer Data Statement" testId="security-page">
    <p>Contractors entrust JobClose AI with sensitive business and customer information. This statement summarizes how we protect that data.</p>
    <H>Encryption</H>
    <ul className="list-disc pl-6 space-y-1">
      <li>All traffic to and from JobClose AI is served over HTTPS (TLS).</li>
      <li>Passwords are hashed using bcrypt and never stored in plaintext.</li>
      <li>Session tokens (JWT) are signed with a server-side secret and rotated when changed.</li>
    </ul>
    <H>Access controls</H>
    <ul className="list-disc pl-6 space-y-1">
      <li>Every contractor account is isolated — data is scoped to the owning user by ID on every read and write.</li>
      <li>Admin endpoints require an admin role and are not accessible to contractors or unauthenticated visitors.</li>
      <li>Customer information (estimates, proposals, contracts, invoices, customer updates) is not publicly accessible — it requires authentication.</li>
    </ul>
    <H>AI processing</H>
    <p>When you trigger an AI feature, only the inputs required for that feature are sent to the AI provider. Outputs are stored within your account. JobClose AI does not use your business data to train third-party models.</p>
    <H>Payments</H>
    <p>Subscription payments are processed by Stripe. JobClose AI does not store full payment-card numbers; we only retain billing references provided by Stripe.</p>
    <H>Backups</H>
    <p>Our database is backed up on a regular schedule. Backups are encrypted at rest.</p>
    <H>Incident response</H>
    <p>If we detect a security incident affecting your data, we will notify affected accounts without undue delay and provide remediation steps.</p>
    <H>Your responsibilities</H>
    <ul className="list-disc pl-6 space-y-1">
      <li>Use a strong, unique password (minimum 8 characters required on signup).</li>
      <li>Do not share your login credentials.</li>
      <li>Review customer-facing documents (proposals, contracts, invoices) before sending.</li>
    </ul>
    <H>Contact</H>
    <p>Security questions or disclosures: <span className="font-mono-jc">security@jobclose.ai</span></p>
  </LegalShell>
);
